import type {
  GridColumnGroupingModel,
  GridApi,
  GridColDef,
  GridColumnGroup,
  GridInitialState,
  GridColumnDimensions,
} from '@repo/go-table';
import { hasValue } from '@repo/utils';
import type { MutableRefObject } from 'react';

import type { TTableSettingsModel } from 'src/services/user-settings-service/types';
import type { TheWellEntity } from 'src/services/wells-service/entities';
import type { Directories } from 'src/store/directories/directories.store';

import { MIN_COLUMN_WIDTH } from './hooks/use-get-table-columns';

export const applySettingsToTable = (
  tableApi: MutableRefObject<GridApi>,
  settings: Omit<TTableSettingsModel, 'ownerUserId'>,
  columnGrouping: GridColumnGroupingModel,
  defaultValues?: { defaultColumnsOrderModel?: string[] },
) => {
  const columns = tableApi.current?.getAllColumns() ?? [];
  const currentState = tableApi.current?.exportState();

  const tableState: GridInitialState = {
    ...currentState,
    columns: {
      ...currentState?.columns,
      columnVisibilityModel: validateColumnsVisibilityModel(columns, settings.columnsVisibility ?? {}),
      orderedFields: getActualOrderModel(
        columnGrouping,
        columns,
        settings.columnsOrderModel ?? defaultValues?.defaultColumnsOrderModel ?? [],
      ),
    },
    filter: {
      ...currentState?.filter,
      filterModel: settings.filterModel ?? { items: [] },
    },
    sorting: {
      ...currentState?.sorting,
      sortModel: settings.sortingModel ?? [],
    },
    pinnedColumns: settings.pinnedColumns ?? {},
  };

  const columnsDimensions = {
    columns: {
      dimensions: columns.reduce(
        (acc, column) => {
          acc[column.field] = {
            width: settings.columnsWidth?.[column.field] ?? column.width ?? MIN_COLUMN_WIDTH,
            flex: column.flex,
            minWidth: column.minWidth ?? MIN_COLUMN_WIDTH,
            maxWidth: -1,
          };
          return acc;
        },
        {} as Record<string, GridColumnDimensions>,
      ),
    },
  };

  tableApi.current?.restoreState(tableState);
  // https://github.com/mui/mui-x/issues/17681 ОЧЕРЕДНАЯ багулина от MUI Data Grid
  tableApi.current?.restoreState(columnsDimensions);
};

export const getActualOrderModel = (
  columnGrouping: GridColumnGroup[],
  columns: GridColDef<TheWellEntity>[],
  _columnsOrderModel: string[],
) => {
  const columnsOrderModel = _columnsOrderModel.filter((column) => columns.find((c) => c.field === column));

  const { columnsByGroups, groupsByColumns } = columnGrouping.reduce(
    (acc, currentGroup) => {
      const processChildren = (group: GridColumnGroup & { parentGroup?: GridColumnGroup }) => {
        group.children = group.children.map((child) => {
          if ('groupId' in child) {
            return processChildren({
              ...child,
              parentGroup: group,
              children: child.children.filter((child) => {
                if ('field' in child) {
                  return !!columns.find((c) => c.field === child.field);
                }

                return true;
              }),
            });
          } else {
            return child;
          }
        });

        group.children.forEach((child) => {
          if ('field' in child) {
            acc.groupsByColumns[child.field] = group;
          }
        });

        acc.columnsByGroups[group.groupId] = group;
        return group;
      };

      processChildren(currentGroup);

      return acc;
    },
    {
      columnsByGroups: {} as Record<string, GridColumnGroup & { parentGroup?: GridColumnGroup }>,
      groupsByColumns: {} as Record<string, GridColumnGroup & { parentGroup?: GridColumnGroup }>,
    },
  );

  const getLastExistingInOrderModelChildIndex = (group: GridColumnGroup, orderModel: string[]) => {
    const lastChildInGroupThatExistsInOrderModel = [...group.children].reverse().find((child): child is GridColDef => {
      if ('field' in child) {
        return orderModel.includes(child.field);
      }

      return false;
    });

    const lastExistingInOrderModelChildIndex = lastChildInGroupThatExistsInOrderModel
      ? orderModel.indexOf(lastChildInGroupThatExistsInOrderModel.field)
      : -1;

    if (lastExistingInOrderModelChildIndex >= 0) {
      return lastExistingInOrderModelChildIndex;
    }

    const parentGroup = columnsByGroups[group.groupId]?.parentGroup;

    if (!parentGroup) {
      return -1;
    }

    return getLastExistingInOrderModelChildIndex(parentGroup, orderModel);
  };

  const actualColumnsOrderModel = (() => {
    const missingColumns = Object.keys(groupsByColumns).filter((column) => !columnsOrderModel?.includes(column));

    const copiedOrderModel = [...columnsOrderModel];

    missingColumns.forEach((column) => {
      const group = groupsByColumns[column];

      if (!group) {
        copiedOrderModel.push(column);
        return;
      }

      const lastExistingInOrderModelChildIndex = getLastExistingInOrderModelChildIndex(group, copiedOrderModel);

      if (lastExistingInOrderModelChildIndex === -1) {
        copiedOrderModel.push(column);
        return;
      }

      copiedOrderModel.splice(lastExistingInOrderModelChildIndex + 1, 0, column);
    });
    return copiedOrderModel;
  })();

  return actualColumnsOrderModel;
};

export const validateColumnsVisibilityModel = (
  columns: GridColDef<TheWellEntity>[],
  columnsVisibility: Record<string, boolean>,
) => {
  const model: Record<string, boolean> = {};

  columns.forEach((column) => {
    model[column.field] = columnsVisibility[column.field] ?? true;
  });

  return model;
};

export const isWellUnplanned = (well: TheWellEntity, directories: Directories) => {
  const stageDictObjects = directories.getObject('GOplan_Dict_WellLifeCycleStage') ?? [];
  const drillingStageId = stageDictObjects.find((dict) => dict.data['slug'] === 'drilling')?.id ?? null;

  if (!hasValue(drillingStageId)) {
    console.warn('cannot find drilling stage id');
    return false;
  }

  const drillingStage = well.data.stages?.find((stage) => stage.wellLifeCycleStageId === drillingStageId);

  return !drillingStage;
};
